import { useEffect, useRef } from "react";

/**
 * Sends a 360 frame's low-res JPG url to the parent window (iframe host)
 * via postMessage. Fires on the initial load and again whenever the images
 * (or the frame index) change.
 *
 * @param {Array}   images                 - the 360 frames array (allImages)
 * @param {Object}  [options]
 * @param {number}  [options.frameIndex=12] - 0-based frame to send (12 = 13th frame)
 * @param {string}  [options.targetOrigin="*"] - parent origin; set to the real
 *                  host (e.g. "https://mainecottage.com") in production
 * @param {boolean} [options.enabled=true]  - gate the sender on/off
 */
export const useSendCurrentImage = (
  images,
  { frameIndex = 12, targetOrigin = "*", enabled = true } = {}
) => {
  // Avoid re-posting the same url on unrelated re-renders.
  const lastSentUrl = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const frame = images?.[frameIndex];
    const frameUrl =
      frame?.jpg_image_low || frame?.jpg_image || frame?.image_low;

    if (!frameUrl || frameUrl === lastSentUrl.current) return;

    lastSentUrl.current = frameUrl;
    console.log(`useSendCurrentImage: sending frame ${frameIndex} url to parent:`, frameUrl);

    window.parent.postMessage(
      {
        type: "360_FRAME_IMAGE",
        frame: frameIndex,
        url: frameUrl,
      },
      targetOrigin
    );
  }, [images, frameIndex, targetOrigin, enabled]);
};
